// ============================================
// Fashion Plate — AI Nurse Persona Dressing System
// Visual prompt builder for the Four-Prompt /
// Two-LLM architecture. Operator speaks a patient
// scenario → nurse gets dressed in real time.
// /fashion-plate
// ============================================

import { useState, useCallback } from 'react';
import { T, F } from '../quality/tokens';
import { ModuleShell } from '../quality/ModuleShell';
import { ModuleHeader } from '../quality/ModuleHeader';
import { GarmentBar } from './fashion-plate/GarmentBar';
import { GarmentInstructor } from './fashion-plate/GarmentInstructor';
import { ConversationView } from './fashion-plate/ConversationView';
import { CURATED_PERSONAS } from '../../data/fashionPlate/curatedPersonas';
import { parseScenario } from '../../services/fashionPlate/scenarioParser';
import { DEFAULT_CAPTURE_CONFIG } from '../../types/fashionPlate';
import type { NursePersona, GarmentState, GarmentPiece, FashionPlatePhase, PromptMakerOutput } from '../../types/fashionPlate';
import type { ContextLayer } from '../../types/patientJourney';

// Persona card accent colors
const PERSONA_COLORS: Record<string, string> = {
  curated_night_nurse: '#6366F1',
  curated_evidence_queen: '#0D9488',
  curated_budget_ally: '#16A34A',
  curated_comeback_coach: '#D97706',
  curated_crisis_responder: '#DC2626',
  curated_proactive_partner: '#2563EB',
  curated_gentle_teacher: '#8B5CF6',
  curated_cycle_companion: '#EC4899',
};

export function FashionPlate() {
  const [phase, setPhase] = useState<FashionPlatePhase>('gallery');
  const [activePersona, setActivePersona] = useState<NursePersona | null>(null);
  const [personaName, setPersonaName] = useState('');
  const [garments, setGarments] = useState<GarmentState[]>([]);
  const [scenarioText, setScenarioText] = useState('');
  const [expandedPiece, setExpandedPiece] = useState<GarmentPiece | null>(null);
  const [instructorPiece, setInstructorPiece] = useState<GarmentPiece | null>(null);
  const [contextSnapshot, setContextSnapshot] = useState<Partial<ContextLayer>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [nurseConfirmation, setNurseConfirmation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectPersona = useCallback((persona: NursePersona) => {
    setActivePersona(persona);
    setPersonaName(persona.name);
    setGarments(persona.garments);
    setContextSnapshot(persona.contextSnapshot);
    setPhase('dressing');
  }, []);

  const handleBackToGallery = useCallback(() => {
    setPhase('gallery');
    setActivePersona(null);
    setPersonaName('');
    setGarments([]);
    setScenarioText('');
    setExpandedPiece(null);
    setInstructorPiece(null);
    setContextSnapshot({});
    setNurseConfirmation(null);
  }, []);

  const handleBackToDressing = useCallback(() => {
    setPhase('dressing');
  }, []);

  const handleSpeakToDress = useCallback(async () => {
    if (!scenarioText.trim() || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await parseScenario(scenarioText);
      setGarments(result.garments);
      setContextSnapshot(result.context);
      setPersonaName(result.name);
      setActivePersona(null);
      setPhase('dressing');
    } catch (err) {
      console.error('Scenario parse failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse scenario. Check your Gemini API key.');
    } finally {
      setIsLoading(false);
    }
  }, [scenarioText, isLoading]);

  const handleInstructorResult = useCallback((output: PromptMakerOutput) => {
    setGarments(output.garmentChanges);
    if (output.contextUpdates) {
      setContextSnapshot((prev) => ({ ...prev, ...output.contextUpdates }));
    }
    setNurseConfirmation(output.nurseConfirmation);
    setInstructorPiece(null);
    setTimeout(() => setNurseConfirmation(null), 4000);
  }, []);

  const handleGarmentTap = useCallback((piece: GarmentPiece) => {
    if (instructorPiece === piece) {
      setInstructorPiece(null);
    } else {
      setInstructorPiece(piece);
      setExpandedPiece(piece);
    }
  }, [instructorPiece]);

  const handleStartConversation = useCallback(() => {
    setPhase('conversation');
  }, []);

  const subtitle = phase === 'gallery'
    ? '8 curated nurse personas'
    : phase === 'conversation'
      ? `Chatting as ${personaName || 'Custom Nurse'}`
      : personaName || 'Custom outfit';

  return (
    <ModuleShell>
      <ModuleHeader module="plate" subtitle={subtitle} />

      {phase === 'gallery' && (
        <GalleryView
          personas={CURATED_PERSONAS}
          onSelect={selectPersona}
          scenarioText={scenarioText}
          onScenarioChange={setScenarioText}
          onSpeakToDress={handleSpeakToDress}
          isLoading={isLoading}
          error={error}
        />
      )}

      {phase === 'dressing' && (
        <DressingRoom
          persona={activePersona}
          personaName={personaName}
          garments={garments}
          expandedPiece={expandedPiece}
          onExpandPiece={setExpandedPiece}
          instructorPiece={instructorPiece}
          onGarmentTap={handleGarmentTap}
          onInstructorResult={handleInstructorResult}
          onInstructorClose={() => setInstructorPiece(null)}
          contextSnapshot={contextSnapshot}
          nurseConfirmation={nurseConfirmation}
          onBack={handleBackToGallery}
          onStartConversation={handleStartConversation}
        />
      )}

      {phase === 'conversation' && (
        <ConversationView
          garments={garments}
          personaName={personaName || 'AI Nurse'}
          contextSnapshot={contextSnapshot}
          captureConfig={DEFAULT_CAPTURE_CONFIG}
          onBack={handleBackToDressing}
          onGarmentTap={handleGarmentTap}
        />
      )}
    </ModuleShell>
  );
}

// ── Gallery View ──

function GalleryView({
  personas,
  onSelect,
  scenarioText,
  onScenarioChange,
  onSpeakToDress,
  isLoading,
  error,
}: {
  personas: NursePersona[];
  onSelect: (p: NursePersona) => void;
  scenarioText: string;
  onScenarioChange: (v: string) => void;
  onSpeakToDress: () => void;
  isLoading: boolean;
  error: string | null;
}) {
  return (
    <div style={{ padding: '0 16px 24px' }}>
      {/* Speak-to-Dress Input */}
      <div style={{
        marginBottom: 20, padding: 16,
        background: T.bgCard, borderRadius: T.radius.lg,
        border: `1px solid ${T.border}`, boxShadow: T.shadowMd,
      }}>
        <div style={{
          fontSize: 10, fontFamily: F.mono, color: '#8B5CF6',
          letterSpacing: 2, fontWeight: 600, marginBottom: 8,
          textTransform: 'uppercase',
        }}>
          SPEAK A SCENARIO
        </div>
        <textarea
          value={scenarioText}
          onChange={(e) => onScenarioChange(e.target.value)}
          placeholder="48-year-old woman, 2am, mid-divorce, scared, low health literacy, tight budget..."
          disabled={isLoading}
          style={{
            width: '100%', minHeight: 72, padding: 12,
            background: T.bgInput, border: `1px solid ${T.borderSoft}`,
            borderRadius: T.radius.md, fontFamily: F.body,
            fontSize: T.fontSize.sm, color: T.text, resize: 'vertical',
            outline: 'none', opacity: isLoading ? 0.6 : 1,
          }}
        />
        <button
          onClick={onSpeakToDress}
          disabled={!scenarioText.trim() || isLoading}
          style={{
            marginTop: 10, width: '100%', height: 44,
            borderRadius: T.radius.md, border: 'none', cursor: 'pointer',
            background: scenarioText.trim() && !isLoading
              ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
              : T.bgInput,
            color: scenarioText.trim() && !isLoading ? '#fff' : T.textGhost,
            fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 600,
            boxShadow: scenarioText.trim() && !isLoading ? '0 4px 20px rgba(139,76,246,0.3)' : 'none',
            transition: 'all 0.2s ease',
          }}
        >
          {isLoading ? 'Dressing the Nurse...' : 'Dress the Nurse'}
        </button>
        {error && (
          <div style={{
            marginTop: 8, padding: '8px 12px',
            fontSize: T.fontSize.xs, color: T.danger,
            fontFamily: F.body, lineHeight: 1.4,
          }}>
            {error}
          </div>
        )}
      </div>

      {/* Curated Cast */}
      <div style={{
        fontSize: 10, fontFamily: F.mono, color: T.accent,
        letterSpacing: 2, fontWeight: 600, marginBottom: 12,
        textTransform: 'uppercase',
      }}>
        CURATED CAST
      </div>

      {personas.map((persona) => {
        const color = PERSONA_COLORS[persona.personaId] ?? '#8B5CF6';
        return (
          <button
            key={persona.personaId}
            onClick={() => onSelect(persona)}
            style={{
              width: '100%', textAlign: 'left',
              marginBottom: 10, padding: 0,
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: T.radius.md, boxShadow: T.shadowSm,
              cursor: 'pointer', overflow: 'hidden',
              transition: 'box-shadow 0.15s',
            }}
          >
            <div style={{ padding: '14px 16px' }}>
              {/* Name + garment preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: T.radius.sm,
                  background: `${color}10`, border: `1px solid ${color}25`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: F.mono, fontSize: 8, fontWeight: 700,
                  color, letterSpacing: 0.3, flexShrink: 0,
                }}>
                  {persona.name.split(' ').slice(1).map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: T.fontSize.base, fontWeight: 600, color: T.text, fontFamily: F.body }}>
                    {persona.name}
                  </div>
                  <div style={{ fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
                    {persona.triggerConditions}
                  </div>
                </div>
              </div>

              {/* Garment icons inline */}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                {persona.garments.map((g) => (
                  <span
                    key={g.piece}
                    title={`${g.piece}: ${g.label}`}
                    style={{
                      fontSize: 9, padding: '2px 6px',
                      borderRadius: T.radius.full,
                      background: T.bgInput, color: T.textSoft,
                      fontFamily: F.mono, fontWeight: 500,
                    }}
                  >
                    {g.label}
                  </span>
                ))}
              </div>

              {/* Conversation style */}
              <div style={{
                fontSize: T.fontSize.xs, color: T.textSoft, fontFamily: F.body,
                marginTop: 8, fontStyle: 'italic', lineHeight: 1.4,
              }}>
                &ldquo;{persona.sampleExcerpt}&rdquo;
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Dressing Room ──

function DressingRoom({
  persona,
  personaName,
  garments,
  expandedPiece,
  onExpandPiece,
  instructorPiece,
  onGarmentTap,
  onInstructorResult,
  onInstructorClose,
  contextSnapshot,
  nurseConfirmation,
  onBack,
  onStartConversation,
}: {
  persona: NursePersona | null;
  personaName: string;
  garments: GarmentState[];
  expandedPiece: GarmentPiece | null;
  onExpandPiece: (p: GarmentPiece | null) => void;
  instructorPiece: GarmentPiece | null;
  onGarmentTap: (p: GarmentPiece) => void;
  onInstructorResult: (output: PromptMakerOutput) => void;
  onInstructorClose: () => void;
  contextSnapshot: Partial<ContextLayer>;
  nurseConfirmation: string | null;
  onBack: () => void;
  onStartConversation: () => void;
}) {
  return (
    <div style={{ padding: '0 16px 24px' }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body,
          padding: '8px 0', marginBottom: 12,
        }}
      >
        &#8592; Back to Gallery
      </button>

      {/* Persona name */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{
          fontSize: T.fontSize.xl, fontWeight: 700, color: T.text,
          fontFamily: F.head,
        }}>
          {personaName || 'Custom Nurse'}
        </div>
        {persona && (
          <div style={{
            fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body,
            marginTop: 4,
          }}>
            {persona.conversationStyle}
          </div>
        )}
      </div>

      {/* Nurse confirmation banner */}
      {nurseConfirmation && (
        <div style={{
          marginBottom: 12, padding: '10px 14px',
          background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
          borderRadius: T.radius.md,
          fontSize: T.fontSize.sm, color: '#7C3AED', fontFamily: F.body,
          fontStyle: 'italic', lineHeight: 1.4,
        }}>
          {nurseConfirmation}
        </div>
      )}

      {/* Garment Bar — the paper doll */}
      <GarmentBar
        garments={garments}
        highlightPiece={instructorPiece ?? expandedPiece}
        onPieceClick={onGarmentTap}
      />

      {/* Garment Instructor — tap-to-instruct panel */}
      {instructorPiece && (
        <GarmentInstructor
          piece={instructorPiece}
          currentGarments={garments}
          currentContext={contextSnapshot}
          onResult={onInstructorResult}
          onClose={onInstructorClose}
        />
      )}

      {/* Garment Rack — expandable details */}
      <div style={{ marginTop: 16 }}>
        <div style={{
          fontSize: 10, fontFamily: F.mono, color: '#8B5CF6',
          letterSpacing: 2, fontWeight: 600, marginBottom: 10,
          textTransform: 'uppercase',
        }}>
          GARMENT RACK
        </div>

        {garments.map((garment) => {
          const isExpanded = expandedPiece === garment.piece;
          return (
            <div
              key={garment.piece}
              style={{
                marginBottom: 8, background: T.bgCard,
                borderRadius: T.radius.md, border: `1px solid ${T.border}`,
                boxShadow: isExpanded ? T.shadowMd : T.shadowSm,
                overflow: 'hidden',
                transition: 'box-shadow 0.2s ease',
              }}
            >
              <button
                onClick={() => onExpandPiece(isExpanded ? null : garment.piece)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: 10, padding: '12px 14px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>
                  {garment.piece === 'shoes' ? '👟' :
                   garment.piece === 'heart' ? '❤️' :
                   garment.piece === 'brain' ? '🧠' :
                   garment.piece === 'hat' ? '🎓' :
                   garment.piece === 'dress' ? '👗' : '👓'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: T.fontSize.sm, fontWeight: 600, color: T.text, fontFamily: F.body }}>
                    {garment.label}
                  </div>
                  <div style={{ fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
                    {garment.contextCategory}
                  </div>
                </div>
                <span style={{
                  fontSize: 9, padding: '2px 8px',
                  borderRadius: T.radius.full,
                  background: garment.intensity === 'max' ? T.accentSoft :
                              garment.intensity === 'full' ? T.successSoft :
                              garment.intensity === 'moderate' ? T.warningSoft : T.bgInput,
                  color: garment.intensity === 'max' ? T.accent :
                         garment.intensity === 'full' ? T.success :
                         garment.intensity === 'moderate' ? T.warning : T.textGhost,
                  fontFamily: F.mono, fontWeight: 600, textTransform: 'uppercase',
                }}>
                  {garment.intensity}
                </span>
                <span style={{ fontSize: 12, color: T.textGhost }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </button>

              {isExpanded && (
                <div style={{
                  padding: '0 14px 14px',
                  borderTop: `1px solid ${T.borderSoft}`,
                }}>
                  <div style={{
                    fontSize: 10, fontFamily: F.mono, color: T.textMuted,
                    letterSpacing: 1, fontWeight: 600, marginTop: 10, marginBottom: 6,
                    textTransform: 'uppercase',
                  }}>
                    ACTIVE VARIABLES
                  </div>
                  {Object.entries(garment.activeVariables).map(([key, val]) => (
                    <div key={key} style={{
                      display: 'flex', justifyContent: 'space-between',
                      padding: '4px 0',
                      borderBottom: `1px solid ${T.borderSoft}`,
                    }}>
                      <span style={{ fontSize: T.fontSize.xs, color: T.textSoft, fontFamily: F.mono }}>
                        {key}
                      </span>
                      <span style={{ fontSize: T.fontSize.xs, color: T.text, fontFamily: F.mono, fontWeight: 600 }}>
                        {val}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Start Conversation Button */}
      <button
        onClick={onStartConversation}
        disabled={garments.length === 0}
        style={{
          marginTop: 20, width: '100%', height: 48,
          borderRadius: T.radius.md, border: 'none', cursor: 'pointer',
          background: garments.length > 0
            ? 'linear-gradient(135deg, #8B5CF6, #6D28D9)'
            : T.bgInput,
          color: garments.length > 0 ? '#fff' : T.textGhost,
          fontFamily: F.body, fontSize: T.fontSize.base, fontWeight: 600,
          boxShadow: garments.length > 0 ? '0 4px 20px rgba(139,76,246,0.3)' : 'none',
          transition: 'all 0.2s ease',
        }}
      >
        Start Conversation
      </button>

      {/* Prompt 4 Preview */}
      <div style={{
        marginTop: 20, padding: 16,
        background: T.bgCard, borderRadius: T.radius.lg,
        border: `1px solid ${T.border}`, boxShadow: T.shadowSm,
      }}>
        <div style={{
          fontSize: 10, fontFamily: F.mono, color: '#8B5CF6',
          letterSpacing: 2, fontWeight: 600, marginBottom: 8,
          textTransform: 'uppercase',
        }}>
          PROMPT 4 — THE OUTFIT
        </div>
        <div style={{
          fontSize: T.fontSize.xs, color: T.textSoft, fontFamily: F.body,
          lineHeight: 1.5,
        }}>
          {persona?.conversationStyle ?? 'Speak a scenario or select a persona to see the prompt layer.'}
        </div>
        {persona?.sampleExcerpt && (
          <div style={{
            marginTop: 10, padding: 12,
            background: T.bgInput, borderRadius: T.radius.md,
            fontSize: T.fontSize.sm, color: T.text, fontFamily: F.body,
            fontStyle: 'italic', lineHeight: 1.5,
          }}>
            &ldquo;{persona.sampleExcerpt}&rdquo;
          </div>
        )}
      </div>
    </div>
  );
}
